import React, { useEffect, useState } from 'react';
import { fetchReviews } from '../lib/cms';

export default function ReviewSlider() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3);
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  useEffect(() => {
    async function loadReviews() {
      const data = await fetchReviews();
      setReviews(data);
      setLoading(false);
    }
    loadReviews();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setItemsPerPage(3);
      } else if (window.innerWidth >= 768) {
        setItemsPerPage(2);
      } else {
        setItemsPerPage(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-none border-4 border-clay-500 border-t-transparent" />
      </div>
    );
  }

  if (reviews.length === 0) {
    return null;
  }

  const maxIndex = Math.max(0, reviews.length - itemsPerPage);

  const next = () => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev === 0 ? maxIndex : prev - 1));
  };

  return (
    <div className="relative w-full">
      {/* Carousel Wrapper */}
      <div className="overflow-hidden py-2">
        <div
          className="flex -mx-3 transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * (100 / itemsPerPage)}%)` }}
        >
          {reviews.map((review) => {
            // Extract text from RICHTEXT_LEXICAL (HTML string)
            const textHtml = review.text?.da || '';
            const plainText = textHtml.replace(/<[^>]*>/g, '');
            const isLong = plainText.length > 160;
            const isExpanded = !!expanded[review.id];

            return (
              <div
                key={review.id}
                className="w-full md:w-1/2 lg:w-1/3 shrink-0 px-3 flex"
              >
                <div className="w-full bg-white p-6 md:p-8 border border-sand-200/60 flex flex-col justify-between shadow-sm rounded-none">
                  <div className="mb-6">
                    {/* Rating Stars */}
                    <div className="flex gap-1 mb-4 text-black text-sm justify-center">
                      {[...Array(review.rating || 5)].map((_, i) => (
                        <i key={i} className="fa-solid fa-star" aria-hidden="true" />
                      ))}
                    </div>

                    {/* Testimonial Text */}
                    <div
                      className={`text-foreground/80 text-sm leading-relaxed italic prose prose-sand text-center mx-auto ${
                        isExpanded ? '' : 'line-clamp-4'
                      }`}
                      dangerouslySetInnerHTML={{ __html: textHtml }}
                    />

                    {isLong && (
                      <button
                        onClick={() => toggleExpand(review.id)}
                        className="mt-3 text-xs font-bold uppercase tracking-wider text-black hover:text-black/70 transition-colors focus:outline-none flex items-center gap-1.5 cursor-pointer mx-auto"
                      >
                        {isExpanded ? (
                          <>
                            Vis mindre <i className="fa-solid fa-chevron-up text-[10px]" />
                          </>
                        ) : (
                          <>
                            Læs mere <i className="fa-solid fa-chevron-down text-[10px]" />
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Author Details */}
                  <div className="border-t border-sand-100 pt-4 mt-auto text-center">
                    <h4 className="font-serif font-bold text-black text-base">
                      {review.author}
                    </h4>
                    {review.role?.da && (
                      <p className="text-xs text-sage-600 font-medium mt-0.5">
                        {review.role.da}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Arrows */}
      {reviews.length > itemsPerPage && (
        <div className="flex justify-center gap-8 mt-8">
          <button
            onClick={prev}
            className="p-3 text-clay-800 hover:text-clay-600 transition-all cursor-pointer text-lg"
            aria-label="Forrige anmeldelse"
          >
            <i className="fa-solid fa-arrow-left" />
          </button>
          <button
            onClick={next}
            className="p-3 text-clay-800 hover:text-clay-600 transition-all cursor-pointer text-lg"
            aria-label="Næste anmeldelse"
          >
            <i className="fa-solid fa-arrow-right" />
          </button>
        </div>
      )}
    </div>
  );
}
